// פעילות אחרונה panel (mock s4, center column) — the 3 newest PAST events,
// from the shared `contact-details:{id}` cache (same endpoint + key the
// timeline tab uses) MERGED with the live notes list, + a quick composer on
// top. The merge (mergeNotesIntoTimeline) is what makes a note saved in the
// composer show up here IMMEDIATELY — the server aggregate only catches up on
// its next revalidation, and dedup-by-noteTime reconciles automatically.
import { useMemo } from "react";
import { Box, Button, Skeleton, Stack, Typography } from "@material-ui/core";
import {
  Call,
  EventOutlined,
  WhatsApp,
  BorderColor,
  HomeOutlined,
  EditOutlined,
  CheckCircleOutline
} from "@material-ui/icons";
import { alpha, useTheme } from "@material-ui/core/styles";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import useSettings from "hooks/useSettings";
import { fToNow } from "utils/formatTime";
import { OverviewPanel } from "./OverviewPanel";
import { QuickNoteComposer } from "./QuickNoteComposer";
import { useSwitchTab } from "./useSwitchTab";
import { mergeNotesIntoTimeline } from "./overviewData";
// Shared org-blur convention (backend masks other users' free text as '****').
import { BLUR_PLACEHOLDER } from "../timeline/timelineData";
import type { DisplayUserNote } from "hooks/useListingNotes";
import type { REST_schema } from "../../../../../../@types/sharedSchema";
import type { Theme } from "@material-ui/core/styles";

type OverviewActivityPanelProps = {
  /** undefined == not in cache yet (cold start) → skeleton, never empty-state flash. */
  details: REST_schema.ContactDetailsResponseSchema | undefined;
  /** True when the details fetch failed and there is no cached value — show an
   *  error text instead of an eternal skeleton. */
  detailsFailed: boolean;
  /** Live notes (own + org) — merged into the feed so composer saves show instantly. */
  notes: DisplayUserNote[];
  addNote: (text: string, options?: { pinned?: boolean }) => Promise<void>;
};

const VISIBLE_EVENTS = 3;

type IconSpec = { Icon: typeof Call; paletteKey: "primary" | "info" | "secondary" | "warning" };

const ACTION_ICONS: Record<string, IconSpec> = {
  call: { Icon: Call, paletteKey: "primary" },
  "message-sent": { Icon: WhatsApp, paletteKey: "info" },
  meeting: { Icon: EventOutlined, paletteKey: "secondary" },
  task: { Icon: CheckCircleOutline, paletteKey: "warning" },
  "favorite-listings-minisite-creation": { Icon: HomeOutlined, paletteKey: "info" },
  note: { Icon: EditOutlined, paletteKey: "warning" },
  signature: { Icon: BorderColor, paletteKey: "secondary" }
};

const iconBoxSx = (theme: Theme, paletteKey: IconSpec["paletteKey"]) => ({
  width: 34,
  height: 34,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  bgcolor: alpha(theme.palette[paletteKey].main, 0.12),
  color: theme.palette[paletteKey].dark
});

export function OverviewActivityPanel({
  details,
  detailsFailed,
  notes,
  addNote
}: OverviewActivityPanelProps): JSX.Element {
  const theme = useTheme();
  const switchTab = useSwitchTab();
  const { user } = useAuth();
  const { userSettings } = useFirestore();
  const { themeDirection } = useSettings();
  const isRTL = themeDirection === "rtl";

  const myName = userSettings?.ownerFullName || user?.displayName || "אני";

  const activity = useMemo(
    () => mergeNotesIntoTimeline(details?.[0]?.timeline ?? [], notes, myName),
    // details identity is stable per cache value; notes is the live hook state.
    [details, notes, myName]
  );
  const visible = activity.slice(0, VISIBLE_EVENTS);

  return (
    <OverviewPanel
      title="פעילות אחרונה"
      headerEnd={
        activity.length > 0 ? (
          <Typography variant="caption" color="text.secondary">
            {Math.min(activity.length, VISIBLE_EVENTS)} אירועים אחרונים
          </Typography>
        ) : undefined
      }
    >
      <Stack sx={{ gap: 1.5 }}>
        <QuickNoteComposer
          placeholder="הוסף הערה או תיעוד שיחה…"
          addNote={addNote}
          withPinToggle={false}
        />

        {visible.length > 0 ? (
          <Stack sx={{ gap: 1.5 }}>
            {visible.map((action, index) => {
              const spec = ACTION_ICONS[action.actionType] ?? ACTION_ICONS.note;
              const { Icon } = spec;
              const isBlurred = action.actionDescription === BLUR_PLACEHOLDER;
              return (
                <Stack key={index} direction="row" sx={{ gap: 1.25 }}>
                  <Box sx={iconBoxSx(theme, spec.paletteKey)}>
                    <Icon sx={{ fontSize: 17 }} />
                  </Box>
                  {/* minWidth:0 lets the flex item shrink; overflowWrap breaks
                      long unbroken tokens in free-text descriptions/usernames
                      so rows never overflow narrow mobile viewports. */}
                  <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                    {isBlurred ? (
                      // Same convention as the timeline tab — never render the
                      // backend's raw '****' org-blur placeholder.
                      <Typography
                        variant="body2"
                        sx={{ color: "text.disabled", fontStyle: "italic" }}
                      >
                        התוכן מוסתר (פעילות של משתמש אחר בארגון)
                      </Typography>
                    ) : (
                      <Typography variant="body2" dir="auto" sx={{ fontWeight: 500 }}>
                        {action.actionDescription}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {action.actionTime ? fToNow(action.actionTime, isRTL) : ""}
                      {action.actionUser?.userName ? ` · ${action.actionUser.userName}` : ""}
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        ) : details === undefined && !detailsFailed ? (
          <Stack sx={{ gap: 1.5 }}>
            {[0, 1, 2].map((row) => (
              <Stack key={row} direction="row" sx={{ gap: 1.25 }} alignItems="center">
                <Skeleton variant="circular" width={34} height={34} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : detailsFailed ? (
          <Typography variant="body2" color="text.secondary">
            לא ניתן לטעון את הפעילות כרגע — נסו לרענן את העמוד.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            אין פעילות מתועדת עדיין — שיחות, הודעות ופגישות יופיעו כאן.
          </Typography>
        )}

        {activity.length > 0 && (
          <Button
            size="small"
            variant="text"
            onClick={() => switchTab("timeline")}
            sx={{ alignSelf: "flex-start" }}
          >
            לציר הזמן המלא ←
          </Button>
        )}
      </Stack>
    </OverviewPanel>
  );
}
